import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { CRMConfiguration } from '../../types/agent';

interface CRMConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: CRMConfiguration) => void;
  initialConfig?: CRMConfiguration;
}

const AVAILABLE_LEAD_FIELDS = [
  { value: 'name', label: 'Nome', description: 'Nome do lead' },
  { value: 'phoneNumber', label: 'Telefone', description: 'Número de telefone' },
  { value: 'email', label: 'Email', description: 'Endereço de email' },
  { value: 'resumeAI', label: 'Resumo IA', description: 'Resumo gerado pela IA' },
  { value: 'status', label: 'Status', description: 'Status do lead' },
  { value: 'leadQuality', label: 'Qualidade do Lead', description: 'Qualidade do lead (Neutro, Frio, Quente)' }
];

const CRMConfigurationDialog: React.FC<CRMConfigurationDialogProps> = ({
  open,
  onClose,
  onSave,
  initialConfig
}) => {
  const theme = useTheme();
  const [config, setConfig] = useState<CRMConfiguration>({
    enabled: false,
    url: '',
    method: 'POST',
    headers: [{ key: 'Content-Type', value: 'application/json' }],
    fieldMappings: [],
    customBody: ''
  });

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    headers: false,
    mappings: false,
    advanced: false
  });

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
    }
  }, [initialConfig]);

  const handleSave = () => {
    if (!config.url.trim()) {
      alert('Por favor, insira a URL do CRM');
      return;
    }

    onSave(config);
    onClose();
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...config.headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setConfig({ ...config, headers: newHeaders });
  };

  const addHeader = () => {
    setConfig({
      ...config,
      headers: [...config.headers, { key: '', value: '' }]
    });
  };

  const removeHeader = (index: number) => {
    const newHeaders = config.headers.filter((_, i) => i !== index);
    setConfig({ ...config, headers: newHeaders });
  };

  const handleFieldMappingChange = (index: number, field: 'leadField' | 'crmField', value: string) => {
    const newMappings = [...config.fieldMappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setConfig({ ...config, fieldMappings: newMappings });
  };

  const addFieldMapping = () => {
    setConfig({
      ...config,
      fieldMappings: [...config.fieldMappings, { leadField: '', crmField: '' }]
    });
  };

  const removeFieldMapping = (index: number) => {
    const newMappings = config.fieldMappings.filter((_, i) => i !== index);
    setConfig({ ...config, fieldMappings: newMappings });
  };

  const generateSampleBody = () => {
    const sampleData: any = {};
    config.fieldMappings.forEach(mapping => {
      if (mapping.leadField && mapping.crmField) {
        const leadField = AVAILABLE_LEAD_FIELDS.find(f => f.value === mapping.leadField);
        sampleData[mapping.crmField] = leadField ? `{{${leadField.value}}}` : `{{${mapping.leadField}}}`;
      }
    });
    return JSON.stringify(sampleData, null, 2);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="primary" />
          <Typography variant="h6">Configuração de Integração CRM</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Configure como os leads serão enviados para seu CRM. Você pode mapear campos do lead para campos específicos do seu sistema.
          </Alert>

          {/* Configuração Básica */}
          <Accordion 
            expanded={expandedSections.basic} 
            onChange={() => toggleSection('basic')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="subtitle1">Configuração Básica</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label="URL do CRM"
                  value={config.url}
                  onChange={(e) => setConfig({ ...config, url: e.target.value })}
                  placeholder="https://seu-crm.com/api/leads"
                  helperText="URL completa do endpoint que receberá os leads"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl sx={{ minWidth: 200, flex: 1 }}>
                    <InputLabel>Método HTTP</InputLabel>
                    <Select
                      value={config.method}
                      onChange={(e) => setConfig({ ...config, method: e.target.value as 'POST' | 'PUT' | 'PATCH' })}
                    >
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enabled}
                        onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                      />
                    }
                    label="Ativar integração"
                    sx={{ alignSelf: 'center' }}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Headers HTTP */}
          <Accordion 
            expanded={expandedSections.headers} 
            onChange={() => toggleSection('headers')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon color="primary" />
                <Typography variant="subtitle1">Headers HTTP</Typography>
                <Chip 
                  size="small" 
                  label={`${config.headers.length} header(s)`}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#000' : 'inherit'
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure os headers necessários para autenticação e formatação da requisição.
                </Typography>
                {config.headers.map((header, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField
                      label="Chave"
                      value={header.key}
                      onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                      sx={{ flex: 1 }}
                      placeholder="Authorization"
                    />
                    <TextField
                      label="Valor"
                      value={header.value}
                      onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                      sx={{ flex: 2 }}
                      placeholder="Bearer token123"
                    />
                    <IconButton onClick={() => removeHeader(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addHeader}
                  variant="outlined"
                  size="small"
                >
                  Adicionar Header
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Mapeamento de Campos */}
          <Accordion 
            expanded={expandedSections.mappings} 
            onChange={() => toggleSection('mappings')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="primary" />
                <Typography variant="subtitle1">Mapeamento de Campos</Typography>
                <Chip 
                  size="small" 
                  label={`${config.fieldMappings.length} campo(s)`}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#000' : 'inherit'
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Mapeie os campos do lead para os campos esperados pelo seu CRM.
                </Typography>
                {config.fieldMappings.map((mapping, index) => (
                  <Card key={index} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl sx={{ minWidth: 200, flex: '1 1 200px' }}>
                          <InputLabel>Campo do Lead</InputLabel>
                          <Select
                            value={mapping.leadField}
                            onChange={(e) => handleFieldMappingChange(index, 'leadField', e.target.value)}
                          >
                            {AVAILABLE_LEAD_FIELDS.map(field => (
                              <MenuItem key={field.value} value={field.value}>
                                <Box>
                                  <Typography variant="body2">{field.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {field.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          sx={{ minWidth: 200, flex: '1 1 200px' }}
                          label="Campo do CRM"
                          value={mapping.crmField}
                          onChange={(e) => handleFieldMappingChange(index, 'crmField', e.target.value)}
                          placeholder="nome_cliente"
                          helperText="Nome do campo no seu CRM"
                        />
                        <IconButton onClick={() => removeFieldMapping(index)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addFieldMapping}
                  variant="outlined"
                  size="small"
                >
                  Adicionar Mapeamento
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Configuração Avançada */}
          <Accordion 
            expanded={expandedSections.advanced} 
            onChange={() => toggleSection('advanced')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon color="primary" />
                <Typography variant="subtitle1">Configuração Avançada</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Para casos mais complexos, você pode definir um corpo personalizado usando as variáveis dos campos mapeados.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Corpo da Requisição (JSON)"
                  value={config.customBody || generateSampleBody()}
                  onChange={(e) => setConfig({ ...config, customBody: e.target.value })}
                  placeholder={generateSampleBody()}
                  helperText="Use {{campo}} para referenciar os campos do lead. Ex: {{name}}, {{phone}}"
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setConfig({ ...config, customBody: generateSampleBody() })}
                    variant="outlined"
                  >
                    Gerar Exemplo
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setConfig({ ...config, customBody: '' })}
                    variant="outlined"
                  >
                    Limpar
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Preview */}
          {config.fieldMappings.length > 0 && (
            <Card sx={{ mt: 2, bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="primary" />
                  Preview da Requisição
                </Typography>
                <Box sx={{ 
                  bgcolor: 'background.default', 
                  p: 2, 
                  borderRadius: 1, 
                  border: 1, 
                  borderColor: 'divider',
                  '& pre': {
                    margin: 0,
                    fontSize: '12px',
                    overflow: 'auto',
                    color: 'text.primary',
                    fontFamily: 'monospace'
                  }
                }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {config.method} {config.url}
                  </Typography>
                  <pre>
                    {config.customBody || generateSampleBody()}
                  </pre>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          sx={{
            color: theme.palette.mode === 'dark' ? '#000' : undefined,
          }}
          startIcon={<CheckCircleIcon />}
          disabled={!config.url.trim()}
        >
          Salvar Configuração
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CRMConfigurationDialog;
